import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { usePO } from '@/hooks/usePOs'
import { useArchivePO, useDeletePO } from '@/hooks/usePOMutations'
import { fmtDate, fmtDateTime, fmtIsoDate } from '@/lib/dates'
import { useUIStore } from '@/store/uiStore'
import { KentuckyPanel } from './KentuckyPanel'
import { LinkRequestModal } from './LinkRequestModal'
import { NotSyncedPill } from './NotSyncedPill'
import { StatusBadge } from './StatusBadge'

interface POExpandedDetailProps {
  poId: string
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(val)
}

const SKIP_REASON: Record<string, string> = {
  sync_disabled: 'sync off',
  offline_mode: 'offline mode',
  missing_credentials: 'no Shopify credentials',
}

// Tooltip for a line's "Not synced" pill, or null when the line is in sync.
// Skips and failures read differently: a skip is expected while sync is off,
// a failure carries Shopify's error so the admin knows what to fix.
function syncTitle(it: {
  shopify_sync_status?: string | null
  shopify_sync_error?: string | null
  shopify_sync_last_attempt?: string | null
}): string | null {
  const when = it.shopify_sync_last_attempt
    ? ` · ${fmtDateTime(it.shopify_sync_last_attempt)}`
    : ''
  if (it.shopify_sync_status === 'skipped') {
    const err = it.shopify_sync_error ?? ''
    return `Skipped: ${SKIP_REASON[err] ?? (err || 'sync off')}${when}`
  }
  if (it.shopify_sync_status === 'failed') {
    return `Failed: ${it.shopify_sync_error || 'unknown error'}${when}`
  }
  return null
}

function csvCell(val: string | number | null | undefined): string {
  if (val == null) return ''
  const s = String(val)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export function POExpandedDetail({ poId }: POExpandedDetailProps) {
  const { data: po, isLoading, isError } = usePO(poId)
  const navigate = useNavigate()
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const archiveMutation = useArchivePO()
  const deleteMutation = useDeletePO()
  const [linkItemId, setLinkItemId] = useState<number | null>(null)

  if (isLoading) {
    return (
      <div className="px-5 py-4 space-y-2 bg-[var(--surface-page)]">
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-32 w-full mt-3" />
      </div>
    )
  }

  if (isError || !po) {
    return (
      <div className="px-5 py-4 text-xs text-[var(--status-rejected-fg)] bg-[var(--surface-page)]">
        Failed to load PO details.
      </div>
    )
  }

  // Sorted copy (never mutate po.items) so the table and the CSV export share
  // one alphabetical order: product name, then variant as the tiebreaker.
  const items = [...(po.items ?? [])].sort(
    (a, b) =>
      (a.product_title ?? '').localeCompare(b.product_title ?? '', undefined, {
        sensitivity: 'base',
      }) ||
      // numeric: sizes are variant titles here — keep "8" ahead of "10".
      (a.variant_title ?? '').localeCompare(b.variant_title ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      })
  )
  const totalUnitsOrdered = items.reduce((s, it) => s + (it.qty_ordered ?? 0), 0)
  const totalCost = items.reduce(
    (s, it) => s + (it.qty_ordered ?? 0) * (it.cost_base ?? 0),
    0
  )
  const status = po.status
  const canReceive = status === 'ORDERED' || status === 'PARTIAL_ORDERED'
  const canEdit = status === 'DRAFT'
  // Past draft but not closed out: the order is fixed, but a waiting
  // customer's request can still be attached to one of its lines.
  const canLinkRequest =
    status === 'AWAITING_APPROVAL' ||
    status === 'APPROVED' ||
    status === 'ORDERED' ||
    status === 'PARTIAL_ORDERED'
  const linkItem = items.find((it) => it.po_item_id === linkItemId)
  const canCancel =
    status === 'DRAFT' ||
    status === 'ORDERED' ||
    status === 'PARTIAL_ORDERED'

  function exportCSV() {
    if (!po) return
    const header = [
      'Barcode',
      'SKU',
      'Product',
      'Variant',
      'Qty ordered',
      'Qty received',
      'Received date',
      'Qty remaining',
      'Unit cost',
      'Extended cost',
    ]
    const rows = items.map((it) => {
      const remaining = Math.max(
        0,
        (it.qty_ordered ?? 0) - (it.qty_received ?? 0)
      )
      const ext = (it.qty_ordered ?? 0) * (it.cost_base ?? 0)
      return [
        it.barcode,
        it.sku,
        it.product_title,
        it.variant_title,
        it.qty_ordered,
        it.qty_received,
        (it.qty_received ?? 0) > 0 ? fmtIsoDate(it.last_received_at) : '',
        remaining,
        it.cost_base?.toFixed(2),
        ext.toFixed(2),
      ]
    })
    const csv = [header, ...rows]
      .map((r) => r.map(csvCell).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const label = po.po_number || po.po_id
    a.href = url
    a.download = `PO-${label}-items.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function cancelPO() {
    const label = po!.po_number || po!.po_id
    const isDraft = status === 'DRAFT'
    const verb = isDraft ? 'Delete' : 'Cancel'
    toast(`${verb} PO ${label}?`, {
      action: {
        label: verb,
        onClick: () => {
          const mut = isDraft ? deleteMutation : archiveMutation
          mut.mutate(po!.po_id, {
            onSuccess: () =>
              toast.success(`${isDraft ? 'Deleted' : 'Cancelled'} ${label}`),
            onError: (err) =>
              toast.error(
                err instanceof Error ? err.message : `Failed to ${verb.toLowerCase()} PO`
              ),
          })
        },
      },
    })
  }

  return (
    <div className="bg-[var(--surface-page)] border-t border-[var(--line-warm)] px-5 py-3">
      {/* Header strip */}
      <div className="flex items-start gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[var(--text-primary)] tabular-nums">
              {po.po_number ? `#${po.po_number}` : po.po_id}
            </span>
            <span className="text-[var(--mute)]">·</span>
            <span className="text-sm text-[var(--text-primary)]">
              {po.supplier || po.vendor || '—'}
            </span>
            {po.supplier && po.vendor && po.supplier !== po.vendor && (
              <span className="text-xs text-[var(--mute)]">({po.vendor})</span>
            )}
            <StatusBadge status={status} className="ml-1" />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[var(--mute)]">
            <span>
              Ordered{' '}
              <span className="text-[var(--ink-2)] tabular-nums">
                {fmtDate(po.ordered_date, '—')}
              </span>
            </span>
            <span>
              Expected{' '}
              <span className="text-[var(--ink-2)] tabular-nums">
                {fmtDate(po.expected_delivery_date, '—')}
              </span>
            </span>
            <span className="tabular-nums">
              {items.length} {items.length === 1 ? 'line' : 'lines'} ·{' '}
              {totalUnitsOrdered} units
            </span>
            <span className="tabular-nums">
              Total{' '}
              <span className="text-[var(--ink-2)] font-medium">
                {formatCurrency(po.total_cost_base ?? totalCost)}
              </span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {canReceive && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() =>
                navigate(
                  '/purchase-orders/' + encodeURIComponent(po.po_id) + '/receive'
                )
              }
            >
              Receive
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setActiveModal('edit', po.po_id)}
            >
              Edit
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={exportCSV}
          >
            Export CSV
          </Button>
          {canCancel && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={cancelPO}
              disabled={deleteMutation.isPending || archiveMutation.isPending}
            >
              Cancel PO
            </Button>
          )}
        </div>
      </div>

      {/* PO-level note + server-managed approval-notes thread, shown only
          when non-empty so the expanded row stays compact. */}
      {((po.notes ?? '').trim() || (po.approval_notes ?? '').trim()) && (
        <div className="mb-3 flex flex-col gap-1 text-[11px]">
          {(po.notes ?? '').trim() && (
            <div>
              <span className="font-semibold uppercase tracking-[0.06em] text-[10px] text-[var(--mute)]">
                Note
              </span>{' '}
              <span className="italic text-[var(--ink-2)]">{po.notes}</span>
            </div>
          )}
          {(po.approval_notes ?? '').trim() && (
            <div>
              <div className="font-semibold uppercase tracking-[0.06em] text-[10px] text-[var(--text-primary)]">
                Approval notes
              </div>
              <div className="whitespace-pre-line leading-[1.6] text-[var(--text-primary)]">
                {po.approval_notes}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Kentucky Horsewear panel — shown once the PO has been transmitted */}
      <KentuckyPanel po={po} className="mb-3 bg-[var(--surface-card)]" />

      {/* Items table */}
      {items.length === 0 ? (
        <div className="text-xs text-[var(--mute)] py-3 text-center border border-dashed border-[var(--line-warm)]">
          No line items on this PO.
        </div>
      ) : (
        <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] max-h-[320px] overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-[var(--surface-header)] sticky top-0">
              <tr className="text-left">
                <th className="tbl-th">
                  Barcode
                </th>
                <th className="tbl-th">
                  SKU · Product
                </th>
                <th className="tbl-th">
                  Variant
                </th>
                <th className="tbl-th text-right">
                  Ordered
                </th>
                <th className="tbl-th text-right">
                  Received
                </th>
                <th className="tbl-th text-right">
                  Remaining
                </th>
                <th className="tbl-th text-right">
                  Unit cost
                </th>
                <th className="tbl-th text-right">
                  Total cost
                </th>
                {canLinkRequest && <th className="tbl-th" />}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const ordered = it.qty_ordered ?? 0
                const received = it.qty_received ?? 0
                const remaining = Math.max(0, ordered - received)
                const ext = ordered * (it.cost_base ?? 0)
                const notSynced = syncTitle(it)
                return (
                  <tr
                    key={it.po_item_id}
                    className="border-t border-[var(--line-warm)]"
                  >
                    <td className="px-2 py-1.5 tabular-nums text-[var(--text-primary)]">
                      {it.barcode || '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="leading-tight">
                        <div className="flex items-center gap-1.5 text-[var(--text-primary)] tabular-nums">
                          {it.sku || '—'}
                          {notSynced && <NotSyncedPill title={notSynced} />}
                        </div>
                        <div className="text-[10px] text-[var(--mute)] truncate max-w-[280px]">
                          {it.product_title}
                        </div>
                        {/* Per-line item note, only when non-empty. */}
                        {(it.notes ?? '').trim() && (
                          <div className="text-[10px] italic text-[var(--mute)] truncate max-w-[280px]">
                            {it.notes}
                          </div>
                        )}
                        {/* Size deleted in Shopify (often recreated under a
                            new variant ID): this line can never sync, so say
                            what to do. Amber = attention (colour-pass). */}
                        {it.variant_deleted && (
                          <div
                            className="text-[10px] font-semibold text-[var(--status-awaiting-fg)] max-w-[280px]"
                            data-testid="variant-deleted-warning"
                          >
                            This size was replaced or removed in Shopify. Re-add
                            the current size to this PO.
                          </div>
                        )}
                        {/* The pill's tooltip as visible text: staff couldn't
                            reliably get the native title to appear. Wraps
                            rather than truncates so the reason stays readable. */}
                        {notSynced && (
                          <div
                            className="text-[10px] text-[var(--mute)] max-w-[280px] break-words"
                            data-testid="sync-reason"
                          >
                            {notSynced}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-[var(--ink-2)]">
                      {it.variant_title || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[var(--text-primary)]">
                      {ordered}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[var(--text-primary)]">
                      {/* "qty / Sep 16, 2026" — qty first at full size, date after,
                          smaller. Both black ink. */}
                      <div className="flex items-baseline justify-end gap-1">
                        <span>{received}</span>
                        {received > 0 && fmtDate(it.last_received_at) && (
                          <span className="text-[10px] whitespace-nowrap">
                            / {fmtDate(it.last_received_at)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[var(--ink-2)]">
                      {remaining}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[var(--ink-2)]">
                      {formatCurrency(it.cost_base)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium text-[var(--text-primary)]">
                      {formatCurrency(ext)}
                    </td>
                    {canLinkRequest && (
                      <td className="px-2 py-1 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[11px] whitespace-nowrap"
                          onClick={() => setLinkItemId(it.po_item_id)}
                        >
                          Link request
                        </Button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {linkItem && (
        <LinkRequestModal
          poId={po.po_id}
          poStatus={status}
          line={linkItem}
          onClose={() => setLinkItemId(null)}
        />
      )}
    </div>
  )
}
